// src/pages/Analytics.js
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Timestamp, collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "../firebase/config";
import { Desk } from "./SeatingPlan";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import {
  GUEST_ATTENDANCE,
  GUEST_CLASSES,
  GUEST_DAILY_ROLLUPS,
  GUEST_DESK_LAYOUTS,
  GUEST_EVENTS,
  GUEST_OBJECTIVES,
  GUEST_STUDENTS,
} from "../utils/guestDummyData";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}

function isWithinRange(ts, startDate, endDate) {
  const t = toMillis(ts);
  if (!t) return false;
  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (t < start.getTime()) return false;
  }
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (t > end.getTime()) return false;
  }
  return true;
}

function ratio(positive, negative) {
  if (positive === 0 && negative === 0) return "–";
  if (negative === 0) return `${positive}:0`;
  const r = (positive / negative).toFixed(2);
  return `${r}:1`;
}

function eventPoints(event) {
  return Number(event?.points) || 1;
}

function formatDateInput(value) {
  return value.toISOString().slice(0, 10);
}

function escapeCsv(value) {
  const text = String(value ?? "");
  if (text.includes("\"") || text.includes(",") || text.includes("\n")) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function dateStringWithinRange(dateString, startDate, endDate) {
  if (!dateString) return false;
  const current = new Date(dateString);
  if (Number.isNaN(current.getTime())) return false;
  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (current < start) return false;
  }
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (current > end) return false;
  }
  return true;
}

function buildTimestampRange(startDate, endDate) {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  if (start) start.setHours(0, 0, 0, 0);
  if (end) end.setHours(23, 59, 59, 999);
  return {
    start: start ? Timestamp.fromDate(start) : null,
    end: end ? Timestamp.fromDate(end) : null,
  };
}

const DESK_WIDTH = 120;
const DESK_HEIGHT = 80;

const Analytics = () => {
  const { isGuest } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id: routeClassId } = useParams();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingClassDetail, setLoadingClassDetail] = useState(false);
  const [error, setError] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedObjectiveIds, setSelectedObjectiveIds] = useState([]);
  const [objectiveSelectionTouched, setObjectiveSelectionTouched] = useState(false);
  const [recentStudentFilterIds, setRecentStudentFilterIds] = useState([]);
  const [recentObjectiveFilterIds, setRecentObjectiveFilterIds] = useState([]);
  const [recentCategoryFilterValues, setRecentCategoryFilterValues] = useState([]);
  const [showRecentFilters, setShowRecentFilters] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceRange, setAttendanceRange] = useState("month");
  const [attendanceStudentFilterIds, setAttendanceStudentFilterIds] = useState([]);
  const [showAttendanceFilters, setShowAttendanceFilters] = useState(false);
  const [attendanceFilterTouched, setAttendanceFilterTouched] = useState(false);
  const heatmapContainerRef = useRef(null);
  const [heatmapScale, setHeatmapScale] = useState(1);
  const [showHeatmapNames, setShowHeatmapNames] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const backToTopTimerRef = useRef(null);
  const [dailyExportMode, setDailyExportMode] = useState("long");

  // Per-class rollups for overview
  const [classRollupsMap, setClassRollupsMap] = useState({});

  // Detail for selected class
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [deskLayout, setDeskLayout] = useState(null);
  const [heatmapLayoutId, setHeatmapLayoutId] = useState("default");
  const [heatmapLayouts, setHeatmapLayouts] = useState([]);
  const [selectedClassRollups, setSelectedClassRollups] = useState([]);

  useEffect(() => {
    setSelectedClassId(routeClassId || "");
  }, [routeClassId]);

  useEffect(() => {
    if (startDate || endDate) return;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  }, [startDate, endDate]);

  // Load authorised classes
  useEffect(() => {
    const load = async () => {
      setLoadingClasses(true);
      setError("");
      try {
        if (isGuest) {
          setClasses(GUEST_CLASSES.filter((cls) => !cls.archived));
          return;
        }

        const user = auth.currentUser;
        if (!user) {
          setError("You must be logged in to view analytics.");
          setLoadingClasses(false);
          return;
        }

        const classesRef = collection(db, "classes");
        const qClasses = query(classesRef, where("teacherId", "==", user.uid));
        const snap = await getDocs(qClasses);
        const cls = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setClasses(cls);
      } catch (e) {
        console.error(e);
        setError("Failed to load analytics data.");
      } finally {
        setLoadingClasses(false);
      }
    };
    load();
  }, [isGuest]);

  useEffect(() => {
    if (!classes.length || !startDate || !endDate) return;

    const loadRollups = async () => {
      try {
        if (isGuest) {
          const map = {};
          classes.forEach((currentClass) => {
            map[currentClass.id] = (GUEST_DAILY_ROLLUPS[currentClass.id] || []).filter((rollup) =>
              dateStringWithinRange(rollup.date, startDate, endDate)
            );
          });
          setClassRollupsMap(map);
          return;
        }

        const map = {};
        await Promise.all(
          classes.map(async (c) => {
            const rollupsRef = collection(db, "classes", c.id, "dailyRollups");
            const rollupsQuery = query(
              rollupsRef,
              where("date", ">=", startDate),
              where("date", "<=", endDate)
            );
            const snap = await getDocs(rollupsQuery);
            map[c.id] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          })
        );
        setClassRollupsMap(map);
      } catch (e) {
        console.error(e);
      }
    };

    loadRollups();
  }, [classes, endDate, isGuest, startDate]);

  // Load detail for selected class
  useEffect(() => {
    if (!selectedClassId || !startDate || !endDate) return;
    const loadDetail = async () => {
      setLoadingClassDetail(true);
      setError("");
      try {
        if (isGuest) {
          setStudents((GUEST_STUDENTS[selectedClassId] || []).filter((student) => !student.archived));
          setEvents((GUEST_EVENTS[selectedClassId] || []).filter((event) =>
            isWithinRange(event.timestamp, startDate, endDate)
          ));
          setObjectives((GUEST_OBJECTIVES[selectedClassId] || []).filter((objective) => !objective.archived));
          setAttendanceRecords((GUEST_ATTENDANCE[selectedClassId] || []).filter((record) =>
            dateStringWithinRange(record.date, startDate, endDate)
          ));
          setSelectedClassRollups((GUEST_DAILY_ROLLUPS[selectedClassId] || []).filter((rollup) =>
            dateStringWithinRange(rollup.date, startDate, endDate)
          ));
          return;
        }

        const studentsRef = collection(db, "classes", selectedClassId, "students");
        const eventsRef = collection(db, "classes", selectedClassId, "events");
        const objectivesRef = collection(db, "classes", selectedClassId, "objectives");
        const attendanceRef = collection(db, "classes", selectedClassId, "attendance");
        const rollupsRef = collection(db, "classes", selectedClassId, "dailyRollups");
        const { start, end } = buildTimestampRange(startDate, endDate);
        const eventQuery = query(
          eventsRef,
          where("timestamp", ">=", start),
          where("timestamp", "<=", end)
        );
        const attendanceQuery = query(
          attendanceRef,
          where("date", ">=", startDate),
          where("date", "<=", endDate)
        );
        const rollupsQuery = query(
          rollupsRef,
          where("date", ">=", startDate),
          where("date", "<=", endDate)
        );

        const [studentsSnap, eventsSnap, objectivesSnap, attendanceSnap, rollupsSnap] = await Promise.all([
          getDocs(studentsRef),
          getDocs(eventQuery),
          getDocs(objectivesRef),
          getDocs(attendanceQuery),
          getDocs(rollupsQuery),
        ]);

        setStudents(
          studentsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((student) => !student.archived)
        );
        setEvents(eventsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setObjectives(objectivesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAttendanceRecords(attendanceSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setSelectedClassRollups(rollupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        setError("Failed to load class details.");
      } finally {
        setLoadingClassDetail(false);
      }
    };
    loadDetail();
  }, [endDate, isGuest, selectedClassId, startDate]);

  useEffect(() => {
    if (!selectedClassId) {
      setHeatmapLayouts([]);
      setHeatmapLayoutId("default");
      return;
    }

    const loadLayouts = async () => {
      try {
        if (isGuest) {
          const layouts = Object.keys(GUEST_DESK_LAYOUTS[selectedClassId] || {})
            .filter((layoutId) => layoutId === "default" || layoutId.startsWith("saved-"))
            .map((layoutId) => {
              if (layoutId === "default") {
                return { id: layoutId, label: "Default layout" };
              }
              const match = layoutId.match(/saved-(\d+)/);
              const index = match ? Number(match[1]) : layoutId.replace("saved-", "");
              return { id: layoutId, label: `Saved Layout ${index}` };
            });
          setHeatmapLayouts(layouts);
          if (!layouts.find((layout) => layout.id === heatmapLayoutId)) {
            setHeatmapLayoutId("default");
          }
          return;
        }

        const layoutsRef = collection(db, "classes", selectedClassId, "deskLayouts");
        const snap = await getDocs(layoutsRef);
        const layouts = snap.docs
          .map((docSnap) => docSnap.id)
          .filter((layoutId) => layoutId === "default" || layoutId.startsWith("saved-"))
          .map((layoutId) => {
            if (layoutId === "default") {
              return { id: layoutId, label: "Default layout" };
            }
            const match = layoutId.match(/saved-(\d+)/);
            const index = match ? Number(match[1]) : layoutId.replace("saved-", "");
            return { id: layoutId, label: `Saved Layout ${index}` };
          })
          .sort((a, b) => {
            if (a.id === "default") return -1;
            if (b.id === "default") return 1;
            return a.label.localeCompare(b.label, undefined, { numeric: true });
          });
        setHeatmapLayouts(layouts);
        if (!layouts.find((layout) => layout.id === heatmapLayoutId)) {
          setHeatmapLayoutId("default");
        }
      } catch (e) {
        console.error(e);
      }
    };

    loadLayouts();
  }, [heatmapLayoutId, isGuest, selectedClassId]);

  useEffect(() => {
    if (!selectedClassId || !heatmapLayoutId) {
      setDeskLayout(null);
      return;
    }

    const loadHeatmapLayout = async () => {
      try {
        if (isGuest) {
          setDeskLayout(
            GUEST_DESK_LAYOUTS[selectedClassId]?.[heatmapLayoutId] || { desks: {}, seating: {} }
          );
          return;
        }

        const layoutRef = doc(db, "classes", selectedClassId, "deskLayouts", heatmapLayoutId);
        const layoutSnap = await getDoc(layoutRef);
        setDeskLayout(
          layoutSnap.exists()
            ? layoutSnap.data()
            : { desks: {}, seating: {} }
        );
      } catch (e) {
        console.error(e);
        setDeskLayout({ desks: {}, seating: {} });
      }
    };

    loadHeatmapLayout();
  }, [heatmapLayoutId, isGuest, selectedClassId]);

  useEffect(() => {
    if (!objectiveSelectionTouched && objectives.length > 0 && selectedObjectiveIds.length === 0) {
      setSelectedObjectiveIds(objectives.map((o) => o.id));
    }
  }, [objectives, selectedObjectiveIds.length, objectiveSelectionTouched]);

  useEffect(() => {
    if (students.length > 0 && recentStudentFilterIds.length === 0) {
      setRecentStudentFilterIds(students.map((s) => s.id));
    }
    if (objectives.length > 0 && recentObjectiveFilterIds.length === 0) {
      setRecentObjectiveFilterIds(objectives.map((o) => o.id));
    }
    if (objectives.length > 0 && recentCategoryFilterValues.length === 0) {
      const categories = Array.from(
        new Set(objectives.map((o) => o.category || "–"))
      );
      setRecentCategoryFilterValues(categories);
    }
    if (!attendanceFilterTouched && students.length > 0 && attendanceStudentFilterIds.length === 0) {
      setAttendanceStudentFilterIds(students.map((s) => s.id));
    }
  }, [
    students,
    objectives,
    recentStudentFilterIds.length,
    recentObjectiveFilterIds.length,
    recentCategoryFilterValues.length,
    attendanceStudentFilterIds.length,
    attendanceFilterTouched,
  ]);

  const heatmapBounds = useMemo(() => {
    if (!deskLayout || !deskLayout.desks) {
      return { width: 0, height: 0 };
    }

    const desks = Object.values(deskLayout.desks);
    if (desks.length === 0) {
      return { width: 0, height: 0 };
    }

    let maxX = 0;
    let maxY = 0;
    desks.forEach((desk) => {
      const x = Number(desk.x) || 0;
      const y = Number(desk.y) || 0;
      maxX = Math.max(maxX, x + DESK_WIDTH);
      maxY = Math.max(maxY, y + DESK_HEIGHT);
    });

    const width = deskLayout.width || maxX;
    const height = deskLayout.height || maxY;

    return { width, height };
  }, [deskLayout]);

  const studentNameMap = useMemo(() => {
    const map = {};
    students.forEach((student) => {
      map[student.id] = student.name || student.id;
    });
    return map;
  }, [students]);

  const heatmapAssignments = useMemo(() => {
    if (!showHeatmapNames || !deskLayout || !deskLayout.seating) return {};
    const assignments = {};
    Object.entries(deskLayout.seating).forEach(([deskId, studentId]) => {
      const name = studentNameMap[studentId];
      if (name) {
        assignments[deskId] = { id: studentId, name };
      }
    });
    return assignments;
  }, [deskLayout, showHeatmapNames, studentNameMap]);

  useEffect(() => {
    if (!deskLayout || !deskLayout.desks) return;
    if (!heatmapBounds.width || !heatmapBounds.height) return;

    const updateScale = () => {
      const container = heatmapContainerRef.current;
      if (!container) return;
      const padding = 16;
      const availableWidth = Math.max(container.clientWidth - padding, 1);
      const availableHeight = Math.max(container.clientHeight - padding, 1);
      const scaleX = availableWidth / heatmapBounds.width;
      const scaleY = availableHeight / heatmapBounds.height;
      const nextScale = Math.min(scaleX, scaleY);
      setHeatmapScale(nextScale);
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [deskLayout, heatmapBounds]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(true);
      if (backToTopTimerRef.current) {
        clearTimeout(backToTopTimerRef.current);
      }
      backToTopTimerRef.current = setTimeout(() => {
        setShowBackToTop(false);
      }, 2000);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (backToTopTimerRef.current) {
        clearTimeout(backToTopTimerRef.current);
      }
    };
  }, []);

  // Overview totals
  const overviewTotals = useMemo(() => {
    let positive = 0;
    let negative = 0;
    Object.values(classRollupsMap).forEach((rollups) => {
      rollups.forEach((rollup) => {
        positive += rollup.positive || 0;
        negative += rollup.negative || 0;
      });
    });
    return { positive, negative, ratio: ratio(positive, negative) };
  }, [classRollupsMap]);

  // Per-class summary for table
  const classSummaries = useMemo(() => {
    return classes.map((c) => {
      const rollups = classRollupsMap[c.id] || [];
      let positive = 0;
      let negative = 0;
      rollups.forEach((rollup) => {
        positive += rollup.positive || 0;
        negative += rollup.negative || 0;
      });
      return {
        id: c.id,
        name: c.name || c.id,
        positive,
        negative,
        ratio: ratio(positive, negative),
      };
    });
  }, [classes, classRollupsMap]);

  // Filtered events for selected class
  const filteredEvents = useMemo(() => {
    return events.filter((e) => isWithinRange(e.timestamp, startDate, endDate));
  }, [events, startDate, endDate]);

  const classTotals = useMemo(() => {
    if (selectedClassRollups.length) {
      const totals = selectedClassRollups.reduce(
        (acc, rollup) => {
          acc.positive += rollup.positive || 0;
          acc.negative += rollup.negative || 0;
          return acc;
        },
        { positive: 0, negative: 0 }
      );
      return {
        positive: totals.positive,
        negative: totals.negative,
        ratio: ratio(totals.positive, totals.negative),
      };
    }

    let positive = 0;
    let negative = 0;
    filteredEvents.forEach((e) => {
      const points = eventPoints(e);
      if (e.type === "positive") positive += points;
      if (e.type === "negative") negative += points;
    });
    return { positive, negative, ratio: ratio(positive, negative) };
  }, [filteredEvents, selectedClassRollups]);

  const dailyBehaviourTotals = useMemo(() => {
    if (selectedClassRollups.length) {
      return selectedClassRollups
        .map((rollup) => ({
          date: rollup.date,
          positive: rollup.positive || 0,
          negative: rollup.negative || 0,
          objective: rollup.objective || 0,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
    }

    const map = {};
    filteredEvents.forEach((e) => {
      const ms = toMillis(e.timestamp);
      if (!ms) return;
      const date = new Date(ms).toISOString().slice(0, 10);
      if (!map[date]) {
        map[date] = { date, positive: 0, negative: 0, objective: 0 };
      }
      const points = eventPoints(e);
      if (e.type === "positive") map[date].positive += points;
      if (e.type === "negative") map[date].negative += points;
      if (e.type === "objective") map[date].objective += 1;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredEvents, selectedClassRollups]);

  const studentBehaviourStats = useMemo(() => {
    const map = {};
    students.forEach((s) => {
      map[s.id] = { studentId: s.id, studentName: s.name || s.id, positive: 0, negative: 0, objective: 0 };
    });
    filteredEvents.forEach((e) => {
      if (!Array.isArray(e.studentIds)) return;
      e.studentIds.forEach((sid) => {
        if (!map[sid]) return;
        const points = eventPoints(e);
        if (e.type === "positive") map[sid].positive += points;
        if (e.type === "negative") map[sid].negative += points;
        if (e.type === "objective") map[sid].objective += 1;
      });
    });
    return Object.values(map).sort((a, b) => b.positive - a.positive || b.negative - a.negative);
  }, [students, filteredEvents]);

  const objectiveLabels = useMemo(() => {
    const map = {};
    objectives.forEach((o) => {
      map[o.id] = o.title || o.id;
    });
    return map;
  }, [objectives]);

  const attendanceInRange = useMemo(() => {
    return attendanceRecords.filter((record) =>
      dateStringWithinRange(record.date, startDate, endDate)
    );
  }, [attendanceRecords, startDate, endDate]);

  const attendanceTotalsByStudent = useMemo(() => {
    const totals = {};
    students.forEach((s) => {
      totals[s.id] = { present: 0, absent: 0 };
    });

    attendanceInRange.forEach((record) => {
      const presentIds = Array.isArray(record.presentStudentIds)
        ? record.presentStudentIds
        : [];
      const absentIds = Array.isArray(record.absentStudentIds)
        ? record.absentStudentIds
        : [];

      presentIds.forEach((id) => {
        if (!totals[id]) return;
        totals[id].present += 1;
      });
      absentIds.forEach((id) => {
        if (!totals[id]) return;
        totals[id].absent += 1;
      });
    });

    return totals;
  }, [attendanceInRange, students]);

  const studentOptions = useMemo(() => {
    return students.map((s) => ({ id: s.id, name: s.name || s.id }));
  }, [students]);

  const attendanceLineConfig = useMemo(() => {
    const palette = [
      "#1E88E5",
      "#E53935",
      "#43A047",
      "#8E24AA",
      "#FB8C00",
      "#00897B",
      "#6D4C41",
      "#3949AB",
      "#C62828",
      "#2E7D32",
    ];
    const dashes = ["", "6 4", "3 3", "10 4", "2 6", "8 2", "4 2", "12 4 2 4"];
    const ids = attendanceStudentFilterIds.length
      ? attendanceStudentFilterIds
      : studentOptions.map((s) => s.id);

    return ids.map((id, index) => {
      const student = studentOptions.find((s) => s.id === id);
      return {
        id,
        name: student?.name || id,
        color: palette[index % palette.length],
        dash: dashes[index % dashes.length],
      };
    });
  }, [attendanceStudentFilterIds, studentOptions]);

  const objectiveOptions = useMemo(() => {
    return objectives.map((o) => ({
      id: o.id,
      title: o.title || o.id,
      category: o.category || "–",
    }));
  }, [objectives]);

  const categoryOptions = useMemo(() => {
    return Array.from(new Set(objectives.map((o) => o.category || "–")));
  }, [objectives]);

  const objectiveColors = useMemo(() => {
    const palette = [
      "#FF9800",
      "#03A9F4",
      "#8BC34A",
      "#E91E63",
      "#9C27B0",
      "#009688",
      "#FFC107",
      "#3F51B5",
    ];
    const map = {};
    objectives.forEach((o, index) => {
      map[o.id] = palette[index % palette.length];
    });
    return map;
  }, [objectives]);

  const objectiveBreakdownByStudent = useMemo(() => {
    if (selectedObjectiveIds.length === 0) return [];
    const map = {};
    students.forEach((s) => {
      map[s.id] = { studentId: s.id, studentName: s.name || s.id };
      selectedObjectiveIds.forEach((oid) => {
        map[s.id][oid] = 0;
      });
    });
    filteredEvents.forEach((e) => {
      if (e.type !== "objective") return;
      if (!selectedObjectiveIds.includes(e.objectiveId)) return;
      if (!Array.isArray(e.studentIds)) return;
      e.studentIds.forEach((sid) => {
        if (!map[sid]) return;
        map[sid][e.objectiveId] += 1;
      });
    });
    return Object.values(map);
  }, [students, filteredEvents, selectedObjectiveIds]);

  const recentObjectiveAchievements = useMemo(() => {
    const objectiveMap = {};
    objectives.forEach((o) => {
      objectiveMap[o.id] = o;
    });
    const studentMap = {};
    students.forEach((s) => {
      studentMap[s.id] = s.name || s.id;
    });

    const itemsByStudent = {};
    students.forEach((s) => {
      itemsByStudent[s.id] = [];
    });

    filteredEvents.forEach((e) => {
      if (e.type !== "objective" || !e.objectiveId) return;
      const timestamp = toMillis(e.timestamp);
      if (!timestamp) return;
      if (!Array.isArray(e.studentIds)) return;
      e.studentIds.forEach((sid) => {
        if (!itemsByStudent[sid]) itemsByStudent[sid] = [];
        itemsByStudent[sid].push({
          studentId: sid,
          studentName: studentMap[sid] || sid,
          objectiveId: e.objectiveId,
          objectiveTitle: objectiveMap[e.objectiveId]?.title || e.objectiveId,
          category: objectiveMap[e.objectiveId]?.category || "–",
          timestamp,
        });
      });
    });

    const studentOk = (sid) =>
      recentStudentFilterIds.length === 0 ||
      recentStudentFilterIds.includes(sid);

    const objectiveOk = (oid) =>
      recentObjectiveFilterIds.length === 0 ||
      recentObjectiveFilterIds.includes(oid);

    const categoryOk = (category) =>
      recentCategoryFilterValues.length === 0 ||
      recentCategoryFilterValues.includes(category);

    return Object.entries(itemsByStudent)
      .filter(([sid]) => studentOk(sid))
      .map(([sid, items]) => {
        const filtered = items.filter(
          (item) => objectiveOk(item.objectiveId) && categoryOk(item.category)
        );
        const lastFive = filtered
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 5)
          .map((item) => ({
            objectiveTitle: item.objectiveTitle,
            category: item.category,
          }));
        return {
          studentId: sid,
          studentName: studentMap[sid] || sid,
          objectives: lastFive,
        };
      });
  }, [
    objectives,
    students,
    filteredEvents,
    recentStudentFilterIds,
    recentObjectiveFilterIds,
    recentCategoryFilterValues,
  ]);

  const attendanceChartData = useMemo(() => {
    if (!attendanceRecords.length || !students.length) return [];

    const rangeDaysMap = {
      week: 7,
      month: 30,
      threeMonths: 90,
      sixMonths: 180,
      twelveMonths: 365,
    };

    const days = rangeDaysMap[attendanceRange] || 30;
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - days);

    const ids = attendanceStudentFilterIds.length
      ? attendanceStudentFilterIds
      : students.map((s) => s.id);
    if (ids.length === 0) return [];

    const byDate = {};
    attendanceRecords.forEach((record) => {
      if (!record.date) return;
      const recordDate = new Date(record.date);
      if (Number.isNaN(recordDate.getTime())) return;
      if (recordDate < cutoff) return;

      const key = record.date;
      if (!byDate[key]) {
        byDate[key] = { date: key };
        ids.forEach((id) => {
          byDate[key][id] = null;
        });
      }

      const presentIds = Array.isArray(record.presentStudentIds)
        ? record.presentStudentIds
        : [];
      const absentIds = Array.isArray(record.absentStudentIds)
        ? record.absentStudentIds
        : [];

      ids.forEach((id) => {
        if (presentIds.includes(id)) {
          byDate[key][id] = 1;
        } else if (absentIds.includes(id)) {
          byDate[key][id] = 0;
        }
      });
    });

    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [attendanceRecords, students, attendanceRange, attendanceStudentFilterIds]);

const heatmapColours = useMemo(() => {
  if (!deskLayout || !deskLayout.seating) return {};

  const scores = {};

  // Step 1: compute raw scores
  Object.entries(deskLayout.seating).forEach(([deskId, studentId]) => {
    let positive = 0;
    let negative = 0;

    filteredEvents.forEach((e) => {
      if (!Array.isArray(e.studentIds)) return;
      if (!e.studentIds.includes(studentId)) return;

      const points = eventPoints(e);
      if (e.type === "positive") positive += points;
      if (e.type === "negative") negative += points;
    });

    scores[deskId] = positive - negative; // raw score
  });

  // Step 2: find min/max for normalisation
  const values = Object.values(scores);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);

  const map = {};

  // Step 3: convert score → colour
  Object.entries(scores).forEach(([deskId, score]) => {
    let t = 0.5; // default neutral

    if (max !== min) {
      t = (score - min) / (max - min); // normalise 0 → 1
    }

    // Step 4: interpolate between red → grey → green
    const red = [244, 67, 54];
    const grey = [180, 180, 180];
    const green = [76, 175, 80];

    let colour;

    if (t < 0.5) {
      // red → grey
      const k = t / 0.5;
      colour = red.map((c, i) => c + (grey[i] - c) * k);
    } else {
      // grey → green
      const k = (t - 0.5) / 0.5;
      colour = grey.map((c, i) => c + (green[i] - c) * k);
    }

    map[deskId] = `rgba(${colour[0]}, ${colour[1]}, ${colour[2]}, 0.8)`;
  });

  return map;
}, [deskLayout, filteredEvents]);

  const selectedClass = classes.find((c) => c.id === selectedClassId) || null;

  const exportStudentTotalsCsv = () => {
    if (!students.length) return;

    const rows = [
      [
        "Student name",
        "Total Positive Points",
        "Total Negative Points",
        "Total Learning Objectives achieved",
        "Attendance %",
      ],
    ];

    const eventTotals = {};
    students.forEach((s) => {
      eventTotals[s.id] = { positive: 0, negative: 0, objective: 0 };
    });

    filteredEvents.forEach((event) => {
      if (!Array.isArray(event.studentIds)) return;
      event.studentIds.forEach((sid) => {
        if (!eventTotals[sid]) return;
        const points = eventPoints(event);
        if (event.type === "positive") eventTotals[sid].positive += points;
        if (event.type === "negative") eventTotals[sid].negative += points;
        if (event.type === "objective") eventTotals[sid].objective += 1;
      });
    });

    students.forEach((student) => {
      const totals = eventTotals[student.id] || { positive: 0, negative: 0, objective: 0 };
      const attendanceTotals = attendanceTotalsByStudent[student.id] || { present: 0, absent: 0 };
      const attendanceDenominator = attendanceTotals.present + attendanceTotals.absent;
      const attendancePercent = attendanceDenominator
        ? ((attendanceTotals.present / attendanceDenominator) * 100).toFixed(1)
        : "";

      rows.push([
        student.name || student.id,
        totals.positive,
        totals.negative,
        totals.objective,
        attendancePercent,
      ]);
    });

    const className = selectedClass?.name || selectedClassId || "class";
    downloadCsv(`student-summary-${className}.csv`, rows);
  };

  const exportStudentDailyCsv = () => {
    if (!students.length) return;

    const eventsByDateStudent = {};
    const datesSet = new Set();

    filteredEvents.forEach((event) => {
      const ms = toMillis(event.timestamp);
      if (!ms) return;
      const dateKey = new Date(ms).toISOString().slice(0, 10);
      datesSet.add(dateKey);

      if (!Array.isArray(event.studentIds)) return;
      event.studentIds.forEach((sid) => {
        if (!eventsByDateStudent[dateKey]) eventsByDateStudent[dateKey] = {};
        if (!eventsByDateStudent[dateKey][sid]) {
          eventsByDateStudent[dateKey][sid] = {
            positive: 0,
            negative: 0,
            objective: 0,
            objectiveTitles: [],
          };
        }
        const bucket = eventsByDateStudent[dateKey][sid];
        const points = eventPoints(event);
        if (event.type === "positive") bucket.positive += points;
        if (event.type === "negative") bucket.negative += points;
        if (event.type === "objective") {
          bucket.objective += 1;
          if (event.objectiveId) {
            const title = objectiveLabels[event.objectiveId] || event.objectiveId;
            bucket.objectiveTitles.push(title);
          }
        }
      });
    });

    const attendanceByDateStudent = {};
    attendanceInRange.forEach((record) => {
      if (!record.date) return;
      datesSet.add(record.date);
      const presentIds = Array.isArray(record.presentStudentIds)
        ? record.presentStudentIds
        : [];
      const absentIds = Array.isArray(record.absentStudentIds)
        ? record.absentStudentIds
        : [];

      if (!attendanceByDateStudent[record.date]) {
        attendanceByDateStudent[record.date] = {};
      }

      presentIds.forEach((id) => {
        attendanceByDateStudent[record.date][id] = "present";
      });
      absentIds.forEach((id) => {
        attendanceByDateStudent[record.date][id] = "absent";
      });
    });

    const dates = Array.from(datesSet).sort();

    if (dailyExportMode === "wide") {
      const headers = ["Student name"];
      dates.forEach((date) => {
        headers.push(`${date} Total Positive Points`);
        headers.push(`${date} Total Negative Points`);
        headers.push(`${date} Total Learning Objectives achieved`);
        headers.push(`${date} Attendance %`);
        headers.push(`${date} Objectives achieved`);
      });

      const rows = [headers];

      students.forEach((student) => {
        const row = [student.name || student.id];
        dates.forEach((date) => {
          const bucket = eventsByDateStudent[date]?.[student.id] || {
            positive: 0,
            negative: 0,
            objective: 0,
            objectiveTitles: [],
          };
          const attendance = attendanceByDateStudent[date]?.[student.id] || "";
          const attendancePercent =
            attendance === "present" ? "100" : attendance === "absent" ? "0" : "";

          row.push(bucket.positive);
          row.push(bucket.negative);
          row.push(bucket.objective);
          row.push(attendancePercent);
          row.push(bucket.objectiveTitles.join("; "));
        });
        rows.push(row);
      });

      const className = selectedClass?.name || selectedClassId || "class";
      downloadCsv(`student-daily-wide-${className}.csv`, rows);
      return;
    }

    const rows = [
      [
        "Student name",
        "Date",
        "Total Positive Points",
        "Total Negative Points",
        "Total Learning Objectives achieved",
        "Attendance %",
        "Objectives achieved",
      ],
    ];

    students.forEach((student) => {
      dates.forEach((date) => {
        const bucket = eventsByDateStudent[date]?.[student.id] || {
          positive: 0,
          negative: 0,
          objective: 0,
          objectiveTitles: [],
        };
        const attendance = attendanceByDateStudent[date]?.[student.id] || "";
        const attendancePercent =
          attendance === "present" ? "100" : attendance === "absent" ? "0" : "";

        rows.push([
          student.name || student.id,
          date,
          bucket.positive,
          bucket.negative,
          bucket.objective,
          attendancePercent,
          bucket.objectiveTitles.join("; "),
        ]);
      });
    });

    const className = selectedClass?.name || selectedClassId || "class";
    downloadCsv(`student-daily-${className}.csv`, rows);
  };

  const applyQuickRange = (range) => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    let start = new Date(now);

    switch (range) {
      case "today":
        start.setHours(0, 0, 0, 0);
        break;
      case "yesterday":
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisWeek": {
        const day = start.getDay();
        const diff = (day + 6) % 7;
        start.setDate(start.getDate() - diff);
        start.setHours(0, 0, 0, 0);
        break;
      }
      case "thisMonth":
        start = new Date(start.getFullYear(), start.getMonth(), 1);
        break;
      case "pastThreeMonths":
        start.setMonth(start.getMonth() - 3);
        start.setHours(0, 0, 0, 0);
        break;
      case "thisYearSoFar": {
        const year = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
        start = new Date(year, 8, 1);
        end.setTime(now.getTime());
        end.setHours(23, 59, 59, 999);
        break;
      }
      default:
        start.setHours(0, 0, 0, 0);
    }

    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  };

  return (
    <div style={{ padding: "1.5rem" }}>
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={{
            ...buttonStyles.primary,
            position: "fixed",
            right: "1.5rem",
            bottom: "1.5rem",
            borderRadius: "999px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.12)",
            zIndex: 1000,
          }}
        >
          Back to top
        </button>
      )}
      <h1>Analytics</h1>

      {isGuest && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "0.9rem 1rem",
            borderRadius: "10px",
            background: "#fff8e1",
            border: "1px solid #f0c36d",
            color: "#5f4300",
          }}
        >
          Recruiter demo mode is using seeded sample behaviour, attendance, and seating data.
        </div>
      )}

      {error && (
        <div style={{ marginBottom: "1rem", color: "red" }}>
          {error}
        </div>
      )}

      <section style={{ ...tileStyle, marginBottom: "1rem" }}>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: "0.9rem" }}>Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyles.input}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.9rem" }}>End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyles.input}
            />
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button type="button" onClick={() => applyQuickRange("today")} style={buttonStyles.chip}>Today</button>
            <button type="button" onClick={() => applyQuickRange("yesterday")} style={buttonStyles.chip}>Yesterday</button>
            <button type="button" onClick={() => applyQuickRange("thisWeek")} style={buttonStyles.chip}>This week</button>
            <button type="button" onClick={() => applyQuickRange("thisMonth")} style={buttonStyles.chip}>This month</button>
            <button type="button" onClick={() => applyQuickRange("pastThreeMonths")} style={buttonStyles.chip}>Past 3 months</button>
            <button type="button" onClick={() => applyQuickRange("thisYearSoFar")} style={buttonStyles.chip}>This year so far</button>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <label style={{ display: "block", fontSize: "0.9rem" }}>Class</label>
            <select
              value={selectedClassId}
              onChange={(e) => {
                const nextClassId = e.target.value;
                navigate(nextClassId ? `/class/${nextClassId}/analytics` : "/analytics");
              }}
              style={inputStyles.select}
            >
              <option value="">All classes (overview)</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Overview mode */}
      {!selectedClassId && (
        <>
          {loadingClasses ? (
            <p>Loading classes…</p>
          ) : (
            <>
              <section
                id="section-summary"
                style={{
                  ...tileStyle,
                  display: "flex",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                  flexWrap: "wrap",
                }}
              >
                <SummaryCard
                  title="Total positives"
                  value={overviewTotals.positive}
                />
                <SummaryCard
                  title="Total negatives"
                  value={overviewTotals.negative}
                />
                <SummaryCard title="Ratio" value={overviewTotals.ratio} />
              </section>

              <section style={tileStyle}>
                <h2>Classes overview</h2>
                {classes.length === 0 ? (
                  <p>No classes found for your account.</p>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "0.5rem",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Class</th>
                        <th style={thStyle}>Positives</th>
                        <th style={thStyle}>Negatives</th>
                        <th style={thStyle}>Ratio</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {classSummaries.map((c) => (
                        <tr key={c.id}>
                          <td style={tdStyle}>{c.name}</td>
                          <td style={tdStyle}>{c.positive}</td>
                          <td style={tdStyle}>{c.negative}</td>
                          <td style={tdStyle}>{c.ratio}</td>
                          <td style={tdStyle}>
                            <button onClick={() => navigate(`/class/${c.id}/analytics`)} style={buttonStyles.secondary}>
                              View class
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </>
      )}

      {/* Class-specific mode */}
      {selectedClassId && (
        <>
          <section style={{ ...tileStyle, marginBottom: "1rem" }}>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={() => navigate("/analytics")} style={buttonStyles.secondary}> 
                ← Back to overview
              </button>
              <button type="button" onClick={() => document.getElementById("section-summary")?.scrollIntoView({ behavior: "smooth" })} style={buttonStyles.secondary}>Summary</button>
              <button type="button" onClick={() => document.getElementById("section-daily-trends")?.scrollIntoView({ behavior: "smooth" })} style={buttonStyles.secondary}>Daily trends</button>
              <button type="button" onClick={() => document.getElementById("section-student-comparison")?.scrollIntoView({ behavior: "smooth" })} style={buttonStyles.secondary}>Student comparison</button>
              <button type="button" onClick={() => document.getElementById("section-objective-breakdown")?.scrollIntoView({ behavior: "smooth" })} style={buttonStyles.secondary}>Objective breakdown</button>
              <button type="button" onClick={() => document.getElementById("section-last-objectives")?.scrollIntoView({ behavior: "smooth" })} style={buttonStyles.secondary}>Last 5 objectives</button>
              <button type="button" onClick={() => document.getElementById("section-heatmap")?.scrollIntoView({ behavior: "smooth" })} style={buttonStyles.secondary}>Desk heatmap</button>
            </div>
          </section>

          <h2>
            Class: {selectedClass ? selectedClass.name || selectedClass.id : selectedClassId}
          </h2>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <button type="button" onClick={exportStudentTotalsCsv} style={buttonStyles.primary}>
              Export student totals CSV
            </button>
            <button type="button" onClick={exportStudentDailyCsv} style={buttonStyles.primary}>
              Export student daily CSV
            </button>
            <label style={{ display: "inline-flex", gap: "0.5rem", alignItems: "center" }}>
              Daily format
              <select
                value={dailyExportMode}
                onChange={(event) => setDailyExportMode(event.target.value)}
                style={inputStyles.select}
              >
                <option value="long">Row per student per date</option>
                <option value="wide">One row per student</option>
              </select>
            </label>
          </div>

          {loadingClassDetail ? (
            <p>Loading class analytics…</p>
          ) : (
            <>
              <section
                style={{
                  ...tileStyle,
                  display: "flex",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                  flexWrap: "wrap",
                }}
              >
                <SummaryCard
                  title="Positives"
                  value={classTotals.positive}
                />
                <SummaryCard
                  title="Negatives"
                  value={classTotals.negative}
                />
                <SummaryCard title="Ratio" value={classTotals.ratio} />
              </section>

              <section id="section-daily-trends" style={{ ...tileStyle, marginBottom: "1.5rem" }}>
                <h3>Daily behaviour trends</h3>
                {dailyBehaviourTotals.length === 0 ? (
                  <p>No events for this class in the selected range.</p>
                ) : (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <LineChart data={dailyBehaviourTotals}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="positive" stroke="#4CAF50" name="Positives" />
                        <Line type="monotone" dataKey="negative" stroke="#F44336" name="Negatives" />
                        <Line type="monotone" dataKey="objective" stroke="#2196F3" name="Objectives" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </section>

              <section id="section-student-comparison" style={{ ...tileStyle, marginBottom: "1.5rem" }}>
                <h3>Student behaviour comparison</h3>
                {studentBehaviourStats.length === 0 ? (
                  <p>No events for this class in the selected range.</p>
                ) : (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <BarChart data={studentBehaviourStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="studentName" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="positive" fill="#4CAF50" name="Positives" />
                        <Bar dataKey="negative" fill="#F44336" name="Negatives" />
                        <Bar dataKey="objective" fill="#2196F3" name="Objectives" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </section>

              <section id="section-objective-breakdown" style={{ ...tileStyle, marginBottom: "1.5rem" }}>
                <h3>Objective breakdown by student</h3>
                {objectives.length === 0 ? (
                  <p>No objectives for this class.</p>
                ) : (
                  <>
                    <div style={{ marginBottom: "0.75rem", display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>Objectives</span>
                      <button
                        type="button"
                        onClick={() => {
                          setObjectiveSelectionTouched(true);
                          setSelectedObjectiveIds(objectives.map((o) => o.id));
                        }}
                        style={buttonStyles.chip}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setObjectiveSelectionTouched(true);
                          setSelectedObjectiveIds([]);
                        }}
                        style={buttonStyles.chip}
                      >
                        Clear
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                      {objectives.map((o) => (
                        <label key={o.id} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={selectedObjectiveIds.includes(o.id)}
                            onChange={(e) => {
                              setObjectiveSelectionTouched(true);
                              if (e.target.checked) {
                                setSelectedObjectiveIds((prev) => [...prev, o.id]);
                              } else {
                                setSelectedObjectiveIds((prev) => prev.filter((id) => id !== o.id));
                              }
                            }}
                          />
                          <span style={{ width: 12, height: 12, background: objectiveColors[o.id], display: "inline-block", borderRadius: 2 }} />
                          <span style={{ fontSize: "0.9rem" }}>{objectiveLabels[o.id]}</span>
                        </label>
                      ))}
                    </div>
                    {objectiveBreakdownByStudent.length === 0 || selectedObjectiveIds.length === 0 ? (
                      <p>No objective events in the selected range.</p>
                    ) : (
                      <div style={{ width: "100%", height: 320 }}>
                        <ResponsiveContainer>
                          <BarChart data={objectiveBreakdownByStudent}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="studentName" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            {selectedObjectiveIds.map((oid) => (
                              <Bar
                                key={oid}
                                dataKey={oid}
                                stackId="objectives"
                                fill={objectiveColors[oid]}
                                name={objectiveLabels[oid]}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                )}
              </section>

              <section id="section-last-objectives" style={{ ...tileStyle, marginBottom: "1.5rem" }}>
                <h3>Last 5 learning objectives achieved</h3>
                <button
                  type="button"
                  onClick={() => setShowRecentFilters((prev) => !prev)}
                  style={{ ...buttonStyles.secondary, marginBottom: "0.75rem" }}
                >
                  {showRecentFilters ? "Hide filters" : "Show filters"}
                </button>
                {showRecentFilters && (
                  <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                  <div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: "0.35rem" }}>
                      Students
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.35rem" }}>
                      <button type="button" onClick={() => setRecentStudentFilterIds(studentOptions.map((s) => s.id))} style={buttonStyles.chip}>Select all</button>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      {studentOptions.map((s) => (
                        <label key={s.id} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={recentStudentFilterIds.includes(s.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRecentStudentFilterIds((prev) => [...prev, s.id]);
                              } else {
                                setRecentStudentFilterIds((prev) => prev.filter((id) => id !== s.id));
                              }
                            }}
                          />
                          <span style={{ fontSize: "0.9rem" }}>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: "0.35rem" }}>
                      Learning objectives
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.35rem" }}>
                      <button type="button" onClick={() => setRecentObjectiveFilterIds(objectiveOptions.map((o) => o.id))} style={buttonStyles.chip}>Select all</button>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      {objectiveOptions.map((o) => (
                        <label key={o.id} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={recentObjectiveFilterIds.includes(o.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRecentObjectiveFilterIds((prev) => [...prev, o.id]);
                              } else {
                                setRecentObjectiveFilterIds((prev) => prev.filter((id) => id !== o.id));
                              }
                            }}
                          />
                          <span style={{ fontSize: "0.9rem" }}>{o.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: "0.35rem" }}>
                      Categories
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.35rem" }}>
                      <button type="button" onClick={() => setRecentCategoryFilterValues(categoryOptions)} style={buttonStyles.chip}>Select all</button>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      {categoryOptions.map((cat) => (
                        <label key={cat} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={recentCategoryFilterValues.includes(cat)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRecentCategoryFilterValues((prev) => [...prev, cat]);
                              } else {
                                setRecentCategoryFilterValues((prev) => prev.filter((c) => c !== cat));
                              }
                            }}
                          />
                          <span style={{ fontSize: "0.9rem" }}>{cat}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  </div>
                )}

                {recentObjectiveAchievements.length === 0 ? (
                  <p>No objective events in the selected range.</p>
                ) : (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "0.5rem",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={thStyle}>Student</th>
                        <th style={thStyle}>Last 5 objectives</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentObjectiveAchievements.map((row) => (
                        <tr key={row.studentId}>
                          <td style={tdStyle}>{row.studentName}</td>
                          <td style={tdStyle}>
                            {row.objectives.length === 0 ? (
                              <span>–</span>
                            ) : (
                              <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                                {row.objectives.map((obj, index) => (
                                  <li key={`${row.studentId}-${index}`}>
                                    {obj.objectiveTitle} ({obj.category})
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section style={{ ...tileStyle, marginBottom: "1.5rem" }}>
                <h3>Attendance over time</h3>
                <button
                  type="button"
                  onClick={() => setShowAttendanceFilters((prev) => !prev)}
                  style={{ ...buttonStyles.secondary, marginBottom: "0.75rem" }}
                >
                  {showAttendanceFilters ? "Hide filters" : "Show filters"}
                </button>
                {showAttendanceFilters && (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: "0.35rem" }}>
                      Filter by students
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.35rem" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setAttendanceFilterTouched(true);
                          setAttendanceStudentFilterIds(studentOptions.map((s) => s.id));
                        }}
                        style={buttonStyles.chip}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAttendanceFilterTouched(true);
                          setAttendanceStudentFilterIds([]);
                        }}
                        style={buttonStyles.chip}
                      >
                        Clear
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      {studentOptions.map((s) => (
                        <label key={s.id} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                          <input
                            type="checkbox"
                            checked={attendanceStudentFilterIds.includes(s.id)}
                            onChange={(e) => {
                              setAttendanceFilterTouched(true);
                              if (e.target.checked) {
                                setAttendanceStudentFilterIds((prev) => [...prev, s.id]);
                              } else {
                                setAttendanceStudentFilterIds((prev) => prev.filter((id) => id !== s.id));
                              }
                            }}
                          />
                          <span style={{ fontSize: "0.9rem" }}>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                  <button type="button" onClick={() => setAttendanceRange("week")} style={buttonStyles.chip}>
                    Past week
                  </button>
                  <button type="button" onClick={() => setAttendanceRange("month")} style={buttonStyles.chip}>
                    Past month
                  </button>
                  <button type="button" onClick={() => setAttendanceRange("threeMonths")} style={buttonStyles.chip}>
                    Last 3 months
                  </button>
                  <button type="button" onClick={() => setAttendanceRange("sixMonths")} style={buttonStyles.chip}>
                    Last 6 months
                  </button>
                  <button type="button" onClick={() => setAttendanceRange("twelveMonths")} style={buttonStyles.chip}>
                    Last 12 months
                  </button>
                </div>
                {attendanceChartData.length === 0 || attendanceLineConfig.length === 0 ? (
                  <p>No attendance records in this period.</p>
                ) : (
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                      <LineChart data={attendanceChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {attendanceLineConfig.map((line) => (
                          <Line
                            key={line.id}
                            type="monotone"
                            dataKey={line.id}
                            stroke={line.color}
                            strokeDasharray={line.dash || undefined}
                            name={line.name}
                            dot={{ r: 2 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </section>

              {deskLayout && deskLayout.desks && (
                <section id="section-heatmap" style={{ ...tileStyle, marginBottom: "1.5rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <h3 style={{ margin: 0 }}>Desk Heatmap</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", color: "#4b5563" }}>
                        <input
                          type="checkbox"
                          checked={showHeatmapNames}
                          onChange={(event) => setShowHeatmapNames(event.target.checked)}
                        />
                        Show student names
                      </label>
                      <label style={{ fontSize: "0.9rem", color: "#4b5563" }}>Layout</label>
                      <select
                        value={heatmapLayoutId}
                        onChange={(event) => setHeatmapLayoutId(event.target.value)}
                        style={{
                          padding: "0.4rem 0.6rem",
                          borderRadius: "6px",
                          border: "1px solid #cfd7e3",
                          fontSize: "0.9rem",
                          background: "white",
                        }}
                      >
                        {heatmapLayouts.map((layout) => (
                          <option key={layout.id} value={layout.id}>
                            {layout.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div
                    ref={heatmapContainerRef}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "2px solid #ccc",
                      background: "#fff",
                      overflow: "hidden",
                      position: "relative",
                      boxSizing: "border-box",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: heatmapBounds.width || 1100,
                        height: heatmapBounds.height || 1000,
                        transform: `scale(${heatmapScale})`,
                        transformOrigin: "top left",
                        backgroundImage: `
                          linear-gradient(to right, #ddd 1px, transparent 1px),
                          linear-gradient(to bottom, #ddd 1px, transparent 1px)
                        `,
                        backgroundSize: "40px 40px",
                      }}
                    >
                      {Object.entries(deskLayout.desks || {}).map(([deskId, desk]) => (
                        <Desk
                          key={deskId}
                          deskId={deskId}
                          x={desk.x}
                          y={desk.y}
                          rotation={desk.rotation}
                          assignedStudent={heatmapAssignments[deskId] || null}
                          teachingMode={false}
                          highlightColour={heatmapColours[deskId]}
                        />
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </>

          )}
        </>
      )}
    </div>
  );
};

const thStyle = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  padding: "0.5rem",
};

const tdStyle = {
  borderBottom: "1px solid #eee",
  padding: "0.5rem",
};

const tileStyle = {
  background: "#f8f8f8",
  border: "1px solid #e2e2e2",
  borderRadius: "0.6rem",
  padding: "1rem",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.06)",
};

const buttonStyles = {
  primary: {
    background: "#4a90e2",
    color: "white",
    border: "none",
    padding: "0.55rem 0.95rem",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondary: {
    background: "white",
    color: "#4a90e2",
    border: "1px solid #4a90e2",
    padding: "0.5rem 0.85rem",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  chip: {
    background: "#f0f4fb",
    color: "#1f4f8b",
    border: "1px solid #c7d7f0",
    padding: "0.35rem 0.7rem",
    borderRadius: "999px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

const inputStyles = {
  input: {
    padding: "0.65rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #cfd7e3",
    fontSize: "1rem",
  },
  select: {
    padding: "0.6rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #cfd7e3",
    fontSize: "1rem",
    background: "white",
  },
};

const SummaryCard = ({ title, value }) => (
  <div
    style={{
      minWidth: "150px",
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      border: "1px solid #ddd",
      background: "#fafafa",
    }}
  >
    <div style={{ fontSize: "0.85rem", color: "#555" }}>{title}</div>
    <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>{value}</div>
  </div>
);

export default Analytics;